import { useState, useEffect, createContext } from "react";
import { User, Auth, Integration } from "../api";
import { hasExpiredToken } from "../utils";

const userController = new User();
const authController = new Auth();
const integrationController = new Integration();

export const AuthContext = createContext();

// Pendiente de volver a la otra app apenas haya sesión acá (ver #sso-to).
const SSO_RETURN_KEY = "ssoReturnToPeer";

// Lee lo que deja la otra app Sustemia en la URL y lo borra de la barra de
// direcciones:
//   #sso=<token>  viene logueado desde la otra app → canjear el token.
//   #sso-to       el usuario tocó "Ingresar con ..." en el login de la otra
//                 app → al tener sesión acá, mandarlo de vuelta logueado.
function takeSsoFromUrl() {
  const { hash } = window.location;
  if (!hash.startsWith("#sso")) return null;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  const match = hash.match(/^#sso=(.+)$/);
  if (match) return decodeURIComponent(match[1]);
  if (hash === "#sso-to") sessionStorage.setItem(SSO_RETURN_KEY, "1");
  return null;
}

// Email de la cuenta que trae el token de traspaso (solo para mostrarlo; la
// validación real la hace el servidor).
function emailFromSsoToken(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part)).email || null;
  } catch (error) {
    return null;
  }
}

// Antes de entrar con un link de la otra app, el usuario confirma la cuenta:
// evita que alguien lo haga trabajar en una cuenta ajena mandándole su link.
function confirmSsoAccount(token) {
  const email = emailFromSsoToken(token);
  return Boolean(email) && window.confirm(`¿Ingresar como ${email}?`);
}

export function AuthProvider(props) {
  const { children } = props;
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [laoding, setLaoding] = useState(true);
  const [ssoError, setSsoError] = useState(null);
  // Si al llegar desde la otra app acá se exige 2FA, el login muestra
  // directamente el formulario del código.
  const [ssoChallenge, setSsoChallenge] = useState(null);

  useEffect(() => {
    (async () => {
      const ssoToken = takeSsoFromUrl();
      if (ssoToken && confirmSsoAccount(ssoToken)) {
        // La sesión que trae la otra app reemplaza a cualquiera que hubiera
        // acá (el servidor también pisa la cookie de refresh).
        authController.removeTokens();
        let signedIn = false;
        try {
          const result = await integrationController.exchange(ssoToken);
          if (result.requires2FA) {
            setSsoChallenge(result.challengeToken);
          } else {
            authController.setAccessToken(result.access, true);
            signedIn = true;
          }
        } catch (error) {
          setSsoError(error.msg || "No se pudo ingresar desde la otra aplicación");
        }
        // Sin sesión nueva, no caer en la vieja de la cookie (sería otra cuenta
        // y taparía el pedido de código o el error).
        if (!signedIn) {
          await authController.serverLogout();
          setLaoding(false);
          return;
        }
      }

      const accessToken = authController.getAccessToken();

      if (!accessToken) {
        // Sin accessToken local — intentar renovar via cookie httpOnly
        await reLogin();
        setLaoding(false);
        return;
      }

      if (hasExpiredToken(accessToken)) {
        // AccessToken expirado — renovar via cookie httpOnly
        await reLogin();
      } else {
        await login(accessToken);
      }

      setLaoding(false);
    })();
  }, []);

  const reLogin = async () => {
    try {
      const { accessToken } = await authController.refreshAccessToken();
      authController.setAccessToken(accessToken);
      await login(accessToken);
    } catch (error) {
      logout();
    }
  };

  const login = async (accessToken) => {
    try {
      const response = await userController.getMe(accessToken);
      delete response.password;

      const primarySite = response.sites?.[0];
      const siteId = primarySite?._id || primarySite;
      const companyId = response.company?._id || response.company;
      if (siteId) localStorage.setItem("site", String(siteId));
      if (companyId) localStorage.setItem("company", String(companyId));

      setUser(response);
      setToken(accessToken);
    } catch (error) {
      logout();
    }
  };

  // "Ingresar con ..." desde el login de la otra app: apenas hay sesión acá
  // (ya existente o recién iniciada), volver allá logueado.
  useEffect(() => {
    if (!token || !sessionStorage.getItem(SSO_RETURN_KEY)) return;
    sessionStorage.removeItem(SSO_RETURN_KEY);
    integrationController
      .handoff(token)
      .then(({ url }) => window.location.assign(url))
      .catch((error) => window.alert(error.msg || "No se pudo volver a la otra aplicación"));
  }, [token]);

  const logout = async () => {
    await authController.serverLogout();
    setUser(null);
    setToken(null);
    authController.removeTokens();
  };

  const data = {
    accessToken: token,
    user,
    login,
    logout,
    ssoError,
    ssoChallenge,
  };

  if (laoding) return null;

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
}
